import { useEffect, useState } from "react"
import { useAuthentication } from "@today/auth"

type LoadingOverlayProps = {
  /**
   * 로그인 확인이 비정상적으로 지연될 때 "세션 초기화" 버튼을 노출할지 여부.
   * 인증 게이트(_app.tsx 의 AuthenticationProvider)에서만 켠다. reset-password 등
   * 같은 컴포넌트를 단순 로딩 표시로 재사용하는 곳에서는 끈 채로 둔다.
   */
  enableSessionReset?: boolean
}

/**
 * 정상 로그인 흐름에서는 곧바로 Keycloak 로 리다이렉트되므로, 오버레이가 이 시간(ms) 이상
 * 유지될 때만 세션 초기화 버튼을 노출해 버튼 깜빡임을 막는다.
 */
const SESSION_RESET_DELAY_MS = 5000

export function LoadingOverlay({ enableSessionReset }: LoadingOverlayProps) {
  const { logout } = useAuthentication()
  const [showReset, setShowReset] = useState(false)
  const [isResetting, setIsResetting] = useState(false)

  useEffect(() => {
    if (!enableSessionReset) return
    const timer = setTimeout(() => setShowReset(true), SESSION_RESET_DELAY_MS)
    return () => clearTimeout(timer)
  }, [enableSessionReset])

  const handleReset = async () => {
    setIsResetting(true)
    try {
      // portal 과 Keycloak clientId·realm SSO 를 공유하므로, next-auth 로컬 세션만 지우면
      // signIn("keycloak") 이 SSO 로 즉시 재로그인해 동일한 멈춤 상태로 되돌아온다.
      // 따라서 federated logout 으로 로컬 세션과 Keycloak SSO 세션을 함께 초기화한다.
      await logout()
    } catch {
      setIsResetting(false)
    }
  }

  return (
    <div
      className="flex h-screen w-full flex-col items-center justify-center gap-6 bg-gray-100"
      style={{ height: "100dvh" }}
    >
      <div className="flex items-center gap-2">
        <div className="h-5 w-5 animate-pulse rounded-full bg-gray-600"></div>
        <div className="h-5 w-5 animate-pulse rounded-full bg-gray-600"></div>
        <div className="h-5 w-5 animate-pulse rounded-full bg-gray-600"></div>
      </div>
      {enableSessionReset && showReset && (
        <div className="flex flex-col items-center gap-2">
          <p className="text-sm text-gray-500">
            로그인 확인이 계속 지연되나요?
          </p>
          <button
            type="button"
            onClick={handleReset}
            disabled={isResetting}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isResetting ? "초기화 중…" : "세션 초기화"}
          </button>
        </div>
      )}
    </div>
  )
}
